import {
	differenceInDays,
	differenceInHours,
	differenceInMinutes,
	differenceInSeconds,
	type Duration,
	formatISO,
	formatISODuration,
	intervalToDuration,
} from 'date-fns'

/**
 * Format relative time in a sensible and human-readable way.
 */
export function formatRelativeTime(locale: Intl.LocalesArgument, date: Date) {
	const now = new Date()

	const seconds = differenceInSeconds(date, now)

	// Within a minute
	if (seconds >= -60 && seconds < 60) {
		const formatter = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' })
		return formatter.format(seconds, 'second')
	}

	const minutes = differenceInMinutes(date, now)

	// Within an hour
	if (minutes >= -60 && minutes < 60) {
		const formatter = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' })
		return formatter.format(minutes, 'minute')
	}

	// Fallback
	const formatter = new Intl.DateTimeFormat(locale, {
		hour: '2-digit',
		minute: '2-digit',
	})
	return formatter.format(date)
}

/**
 * Format relative date in a sensible and human-readable way.
 */
export function formatRelativeDate(locale: Intl.LocalesArgument, date: Date) {
	const now = new Date()

	// Force current time to midnight for correct day difference calculation.
	now.setHours(0, 0, 0, 0)

	const days = differenceInDays(date, now)

	// Within a few days
	if (days > -3 && days < 3) {
		const formatter = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' })
		return formatter.format(days, 'day')
	}

	// Within a week
	if (days > 0 && days < 7) {
		const formatter = new Intl.DateTimeFormat(locale, { weekday: 'long' })
		return formatter.format(date)
	}

	// Within the same year
	if (date.getFullYear() === now.getFullYear()) {
		const formatter = new Intl.DateTimeFormat(locale, {
			month: 'short',
			day: 'numeric',
		})
		return formatter.format(date)
	}

	// Fallback
	const formatter = new Intl.DateTimeFormat(locale, {
		year: 'numeric',
		month: 'short',
		day: 'numeric',
	})
	return formatter.format(date)
}

/**
 * Format relative date and time in a sensible and human-readable way.
 */
export function formatRelativeDateTime(
	locale: Intl.LocalesArgument,
	date: Date,
) {
	const now = new Date()

	const hours = differenceInHours(date, now)

	// Within a day
	if (hours > -24 && hours < 24) {
		const formatter = new Intl.DateTimeFormat(locale, {
			hour: '2-digit',
			minute: '2-digit',
		})
		return formatter.format(date)
	}

	const days = differenceInDays(date, now)

	// Within a week
	if (days > -7 && days < 7) {
		const formatter = new Intl.DateTimeFormat(locale, { weekday: 'short' })
		return formatter.format(date)
	}

	// Within the same year
	if (date.getFullYear() === now.getFullYear()) {
		const formatter = new Intl.DateTimeFormat(locale, {
			month: 'short',
			day: 'numeric',
		})
		return formatter.format(date)
	}

	// Fallback
	const formatter = new Intl.DateTimeFormat(locale, {
		year: 'numeric',
		month: 'short',
		day: 'numeric',
	})
	return formatter.format(date)
}

export function getDateIntervalDuration(
	start: Date | number,
	end: Date | number,
) {
	return intervalToDuration({ start, end })
}

/**
 * Format time duration object to `HH:mm:ss`.
 */
export function formatTimeDuration(duration: Duration) {
	const { days = 0, hours = 0, minutes = 0, seconds = 0 } = duration

	const formattedHours = (hours + days * 24).toString().padStart(2, '0')
	const formattedMinutes = minutes.toString().padStart(2, '0')
	const formattedSeconds = seconds.toString().padStart(2, '0')

	return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`
}

/**
 * Parse time duration string with the format `HH:mm:ss`.
 */
export function parseTimeDuration(duration: string) {
	const [seconds = 0, minutes = 0, hours = 0] = duration
		.split(':')
		.map((item) => parseInt(item) || 0)
		.toReversed()

	return seconds + minutes * 60 + hours * 60 * 60
}

/**
 * Format date interval to `P(n)Y(n)M(n)DT(n)H(n)M(n)S`.
 */
export function dateIntervalToISOString(
	start: Date | number,
	end: Date | number,
) {
	const duration = getDateIntervalDuration(start, end)
	return formatISODuration(duration)
}

/**
 * Format date to `YYYY-MM-DD` in user's local time zone
 */
export function dateToISOString(date: Date) {
	return formatISO(date, { representation: 'date' })
}

/**
 * Format date to `HH:mm:ssZ/±HH:mm` in user's local time zone
 */
export function timeToISOString(date: Date) {
	return formatISO(date, { representation: 'time' })
}

/**
 * Get difference between dates in various units
 */
export function getDateDifference(
	later: Date,
	earlier: Date,
	unit: 'seconds' | 'minutes',
) {
	switch (unit) {
		case 'seconds': {
			return differenceInSeconds(later, earlier)
		}

		case 'minutes': {
			return differenceInMinutes(later, earlier)
		}
	}
}
